import {Component, State, Prop, Event, EventEmitter, h, Listen} from '@stencil/core';
import { overrideSettings } from "../../utils/utils";
import { v4 as uuidv4 } from 'uuid';

@Component({
  tag: 'fdi-multiselect',
  styleUrl: 'fdi-multiselect.scss',
  shadow: true,
})

export class FDIMultiselect {

  @Prop() __value: string = '';
  @Prop() __settings: any;
  @Prop() __opts: any = [];
  @Prop() __selected: any;

  @State() inputValue: string = '';
  @State() inputFocused: boolean = false;
  @State() selected: any = []; 
  @State() textInput: HTMLFdiTextInputElement;
  @State() blurTimeout: NodeJS.Timeout;
  @State() opts: any = [];
  @State() showOptions: boolean = false;
  @State() settings: any = {
    disabled: false,
    tagMode: true,
    sortOptions: true,
    selectAllButton:{
      text: 'Select All',
      enabled: true,
      style: {
        outline: 'solid 1px #ccc',
        background: 'white',
        color: '#5f5f5f'
      }
    },
    removeAllButton:{
      text: 'Remove All',
      enabled: true,
      style: {
        outline: 'solid 1px #ccc',
        background: 'white',
        color: '#5f5f5f'
      }
    },
    addCustomButton: {
      text: 'Add Custom Value',
      enabled: true,
      style: {
        outline: 'solid 1px #ccc',
        background: 'white',
        color: '#5f5f5f'
      }
    },
    tooltipEnabled: false,
    singleSelect: false,
    closeOnOptionClick: true,
    defaults: [],
    style: {
      general: {
        fontSize: '15px',
        maxWidth: '100%',
        width: '100%',
        margin: 0,
        outline: '1px solid #ccc',
        background: 'white',
      },
      optionSelection: {
        width: '100%',
        maxHeight: '200px',
      },
    },
    input: {
      placeholder: 'Enter Value',
      fontSize: '15px',
    },
    meta: {},
  };

  @Listen('fdi_wc_input_change')
  fdi_wc_input_change__handler( ev ){
    let data = ev.detail;
    this.inputValue = data.newValue;
  }

  @Listen( 'keydown', { target: 'document' } )
  handleKeyDown( ev: KeyboardEvent ){
    if ( ev.key === 'Enter' && this.inputFocused ){
      this.addCustomValue();
    }
  }

  @Event({
    eventName: 'fdi_wc_multiselect_change',
    bubbles: true,
    cancelable: true,
  }) multiselectChanged: EventEmitter

  emitData(){
    let selected = this.selected.map( ( selected ) => {
      return this.isObj( selected ) ? selected.value : selected;
    });
    this.multiselectChanged.emit({
      newValue: selected,
      meta: this.settings.meta,
    });
  }

  onInputFocus(){
    this.showOptions = true;
    this.inputFocused = true;
  }

  onInputBlur() {
    this.blurTimeout = setTimeout( () => {
      this.showOptions = false;
      this.inputFocused = false;
    }, 250 );
  }

  inSelected( o ){
    for ( const selected of this.selected ){
      if ( JSON.stringify( selected ) === JSON.stringify( o ) ){
        return true;
      }
    }
    return false;
  }

  inOpts( val ){
    for ( const [i, opt] of this.opts.entries() ){
      let optVal = this.isObj( opt ) ? opt.value : opt;
      if ( optVal === val ){
        return i;
      }
    }
    return -1;
  }

  optionsClickHandler( o ){
    if ( !this.inSelected( o ) ){
      if ( this.settings.singleSelect ){
        this.selected = [
          ...[ o ]
        ];
      }
      else{
        this.selected = [
          ...this.selected,
          ...[ o ]
        ];
      }
      this.emitData();
    }
    // Cancel menu close since an item was selected. This only works because the blur event is first
    if (!this.settings.closeOnOptionClick) {
      clearTimeout(this.blurTimeout);
      this.blurTimeout = null;
      
      this.textInput.focusInput();
    }
  }

  selectAllOpts(){
    this.selected = [
      ...this.opts
    ];
    this.emitData();
  }

  removeAllOpts(){
    this.selected = [
      ...[]
    ];
    this.emitData();
  }

  removeSelectionHandler( i ){
    let selectedArr = this.selected;
    selectedArr.splice( i, 1 );
    this.selected = [ ...selectedArr ];
    this.emitData();
  }

  addCustomValue(){
    if ( this.inputValue && !this.inSelected( this.inputValue ) ){
      if ( this.settings.singleSelect ){
        this.selected = [
          ...[ this.inputValue ],
        ];
      }
      else{
        this.selected = [
          ...this.selected,
          ...[ this.inputValue ],
        ];
      }
      this.emitData();
    }
  }

  componentWillLoad(){
    let settings = this.__settings;
    let selected = this.__selected;
    let opts = this.__opts;

    if ( typeof settings === 'string' ){
      settings = JSON.parse( settings );
    }

    if ( typeof selected === 'string' ){
      selected = JSON.parse( selected );
    }

    if ( typeof opts === 'string' ){
      opts = JSON.parse( opts );
    }

    this.settings = overrideSettings( this.settings, settings );
    selected = selected || [];
    selected = selected.filter( ( s ) => {
      return s !== null && s !== undefined && s.toString().trim() !== '';
    });
    if ( this.settings.sortOptions ){
      opts.sort( ( a, b ) => {
        a = String( a ).toUpperCase();
        b = String( b ).toUpperCase();
        if ( a > b ) return 1;
        if ( a < b ) return -1;
        return 0;
      });
    }
    this.opts = [
      ...( opts || [] )
    ];
    let defaults = Object.values( this.settings.defaults || [] ) || [];
    let defaultsLength = defaults.length;
    let originalSelectedLength = this.selected.length;
    this.selected.forEach( ( selectedOption, i ) => {
      let selectedIdx = this.inOpts( selectedOption );
      if ( selectedIdx > -1 ){
        selected.splice( i, 1 );
        selected = [
          ...selected,
          ...[ this.opts[selectedIdx] ]
        ];
      }
      else{
        this.opts = [
          ...this.opts,
          ...[ selectedOption ],
        ];
      }
    });
    this.selected = [
      ...( selected || [] )
    ];
    for ( const opt of this.opts ){
      let optVal = this.isObj( opt ) ? opt.value : opt;
      if ( originalSelectedLength === 0 && defaultsLength > 0 && defaults.includes( optVal ) ){
        this.selected = [
          ...this.selected,
          ...[ opt ],
        ];
      }
    }
    this.emitData();
  }

  isObj( variable ){
    return {}.toString.call( variable ) === '[object Object]' && variable !== null;
  }

  render() {
    let optionSelectionSettings = {
      ...this.settings.style.optionSelection,
      ...{
        display: this.showOptions ? 'block' : 'none',
      }
    }

    /**
     * Set the choices.
     */
    let options = this.opts.map( ( op ) => {
      let isObject = this.isObj( op );
      let opVal = isObject ? op.value : op;
      let opDisplay = isObject ? op.display : op;
      if ( this.inputValue ){
        if ( !opVal.toLowerCase().includes( this.inputValue.toLowerCase() ) ) return;
      }
      let optionClasses = ['option'];

      if ( this.inSelected( op ) ){
        optionClasses.push( 'selected' );
      }
      return (
        <div onClick={ () => this.optionsClickHandler( op ) } class={ optionClasses.join( ' ' ) }>
          <span innerHTML={ opDisplay }> </span>
        </div>
      );
    });

    /**
     * Set the menu.
     */
    let menu = '';
    if ( !this.settings.disabled ){
      menu = (
        <div class={ 'menu' }>
          {
            !this.settings.singleSelect && this.opts.length > 0 && this.settings.selectAllButton.enabled &&
            <div
              onClick={ () => this.selectAllOpts() }
              style={ this.settings.selectAllButton.style }
            >
              { this.settings.selectAllButton.text }
            </div>
          }
          {
            this.settings.removeAllButton.enabled && this.selected.length > 0 &&
            <div
              onClick={ () => this.removeAllOpts() }
              style={ this.settings.removeAllButton.style }
            >
              { this.settings.removeAllButton.text }
            </div>
          }
          {
            this.settings.addCustomButton.enabled && this.inputValue &&
            <div
              onClick={ () => this.addCustomValue() }
              style={ this.settings.addCustomButton.style }
            >
              { this.settings.addCustomButton.text }
            </div>
          }
        </div>
      );
    }

    let optionSelection = (
      <div id={ 'optionsContainer' } style={ optionSelectionSettings }>
        { options }
      </div>
    );

    let selectedChoices = this.selected.map( ( selected, i ) => {
      let selectedClasses = [
        'selected',
      ];
      let isObj = this.isObj( selected );
      this.settings.tagMode ? selectedClasses.push( 'tag-mode' ) : selectedClasses.push( 'list-mode' );
      let tooltipSettings = {
        tooltipText: isObj ? ( selected.tooltip || selected.display ) : selected,
      };

      return (
        <div class={ selectedClasses.join( ' ' ) } >
          {
            this.settings.tooltipEnabled &&
            <fdi-tooltip key={ uuidv4() } __settings={ tooltipSettings } />
          }
          {
            !this.settings.disabled &&
            <div
              onClick={ () => this.removeSelectionHandler( i ) }
              class={ 'removeSelected' }
              innerHTML={ '&times;' }>
            </div>
          }
          <div class={ 'selectedContent' }>{ isObj ? selected.display : selected }</div>
        </div>
      );
    });

    let selectedContainerStyle = {
      flexFlow: this.settings.tagMode ? 'wrap' : 'column',
    }
    let selected = (
      <div id={ 'selectedContainer' } style={ selectedContainerStyle }>
        { selectedChoices }
      </div>
    );

    return (
      <div class={ 'fdi wc multiselect' } style={ this.settings.style.general }>
        {
          !this.settings.disabled &&
          <div>
            <fdi-text-input
              onBlur={ () => { this.onInputBlur() }}
              onFocus={ () => { this.onInputFocus() } }
              __settings={ this.settings.input }
              ref={(el) => this.textInput = el }
            />
          </div>
        }
        { menu }
        { optionSelection }
        { selected }
      </div>
    );
  }
}
